import { CalendarToday } from "@mui/icons-material";
import { Box, Button, Popover, Typography as T } from "@mui/material";
import { DateRange, DateRangePicker } from "date-range-picker-mui";
import dayjs from "dayjs";
import { useState } from "react";
import { calendarDefaults } from "../../../constants/calendarDefaults";

const LONG_DATE_FORMAT = "DD MMM YYYY";
const FILTER_DATE_FORMAT = "YYYY-MM-DD";
const DEFAULT_START_DATE = dayjs("2020-01-01 00:00");

export type DynamicDateRange = {
  label?: string;
} & DateRange;

type DateRangeFilterProps = {
  dateRange?: DynamicDateRange | null;
  onDateRangeSelected: (range: DynamicDateRange) => void;
};

const DateRangeFilter = ({ onDateRangeSelected, dateRange }: DateRangeFilterProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Button text based on the selected date range
  const buttonText =
    dateRange?.startDate && dateRange?.endDate
      ? `${dayjs(dateRange.startDate).format(LONG_DATE_FORMAT)}${
          !dayjs(dateRange.startDate).isSame(dateRange.endDate)
            ? ` — ${dayjs(dateRange.endDate).format(LONG_DATE_FORMAT)}`
            : ""
        }`
      : "January 2020 - Present"; // Default text when no date range is selected

  const handleChange = (range: DynamicDateRange) => {
    setAnchorEl(null);
    onDateRangeSelected({
      ...range,
    });
  };

  return (
    <div>
      <Button
        fullWidth
        variant="outlined"
        startIcon={<CalendarToday />}
        sx={{
          padding: "5px 8px",
          "& .MuiButton-startIcon": {
            marginRight: "12px !important",
          },
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {buttonText}
      </Button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ marginBottom: 2, "& .MuiPaper-root": { boxShadow: "none" } }}>
          <DateRangePicker
            open={!!anchorEl}
            toggle={() => setAnchorEl(null)}
            definedRanges={calendarDefaults}
            onChange={handleChange}
            initialDateRange={dateRange ?? undefined}
            minDate={DEFAULT_START_DATE.toDate()}
            maxDate={dayjs().endOf("day").toDate()}
          />
          <T textAlign="center" fontStyle="italic">
            Date range must be between {DEFAULT_START_DATE.format(LONG_DATE_FORMAT)} and today.
          </T>
        </Box>
      </Popover>
    </div>
  );
};

export default DateRangeFilter;
